/**
 * Module visibility helpers for RBAC UI and runtime UI gating.
 * GET /permission/me returns enabledModules (RBAC module enable) separately from permissions.
 */

import { CHAT_VIEW_PERMISSIONS } from "./chatPermissions";

/** UI label "Message(s) Management" maps to RBAC module `chat`, not `message`. */
export const MODULE_KEY_ALIASES = {
  message: "chat",
  messages: "chat",
};

/** Actions that gate menus / hydrate permissions (chat uses `access`, not `read`). */
export const MODULE_MENU_GATE_ACTIONS = new Set([
  "read",
  "view",
  "access",
  "read_student",
  "read_staff",
]);

/** @deprecated Use MODULE_MENU_GATE_ACTIONS */
export const MODULE_VIEW_ACTIONS = MODULE_MENU_GATE_ACTIONS;

/** Derive permission module key from slug (e.g. support.email → support). */
export function getModuleKeyFromPermissionSlug(slug) {
  if (!slug || typeof slug !== "string") return null;
  const trimmed = slug.trim();
  if (!trimmed) return null;
  return trimmed.split(".")[0] || null;
}

/**
 * RBAC module gate: null/undefined enabledModules = backward compat (all modules enabled).
 */
function normalizeModuleKey(slugOrModuleKey) {
  const raw = String(slugOrModuleKey || "").trim();
  const key = raw.includes(".")
    ? getModuleKeyFromPermissionSlug(raw)
    : raw.toLowerCase();
  if (!key) return "";
  return MODULE_KEY_ALIASES[key] || key;
}

export function isModuleEnabled(enabledModules, slugOrModuleKey) {
  if (enabledModules == null) return true;
  if (!Array.isArray(enabledModules)) return true;
  const key = normalizeModuleKey(slugOrModuleKey);
  if (!key) return true;
  const raw = String(slugOrModuleKey || "").trim();
  return enabledModules.some((m) => {
    const mod = String(m || "").toLowerCase();
    return mod === key || mod === raw.toLowerCase() || mod === MODULE_KEY_ALIASES[mod];
  });
}

/**
 * UI visibility: module enabled (RBAC) AND user holds the exact permission slug (or allowed menu gate).
 * Create/initiate slugs (e.g. chat.group, chat.one_to_one) are never implied from view-only grants.
 */
export function isPermissionVisible(enabledModules, permissions, slug) {
  if (!slug) return false;
  if (!isModuleEnabled(enabledModules, slug)) return false;
  const permList = Array.isArray(permissions) ? permissions : [];
  if (permList.includes(slug)) return true;

  const mod = getModuleKeyFromPermissionSlug(slug);
  const action = String(slug).split(".").pop() || "";
  const hasAnyInModule = mod && permList.some((p) => getModuleKeyFromPermissionSlug(p) === mod);

  // Same-module menu gates only (read/view/access), not create/update/delete.
  if (hasAnyInModule && MODULE_MENU_GATE_ACTIONS.has(action)) {
    return true;
  }

  // Messages menu: chat.access if user has any view-only chat permission.
  if (
    slug === "chat.access" &&
    isModuleEnabled(enabledModules, "chat") &&
    CHAT_VIEW_PERMISSIONS.some((p) => permList.includes(p))
  ) {
    return true;
  }

  return false;
}

/**
 * Permissions that gate module visibility for RBAC enable/disable.
 * Prefer read/view; modules like support (email, chat only) use all actions as gates.
 */
export function getModuleGatePermissionDetails(module) {
  const details = module?.permissionDetails || [];
  if (!details.length) return [];
  const viewPerms = details.filter((p) =>
    MODULE_VIEW_ACTIONS.has(p.formKey || p.action)
  );
  return viewPerms.length > 0 ? viewPerms : details;
}

/** @param {Array<{ formKey?: string, action?: string }>} permissionDetails */
export function getModuleViewPermission(permissionDetails) {
  if (!Array.isArray(permissionDetails)) return null;
  return (
    permissionDetails.find((p) => MODULE_VIEW_ACTIONS.has(p.formKey || p.action)) ||
    null
  );
}

/** @param {object} role role detail with RolePermissions */
export function buildEnabledModulesFromRole(role, modules) {
  const roleSlugs = new Set();
  (role?.RolePermissions || role?.role_permissions || []).forEach((rp) => {
    const permission = rp.Permission || rp.permission;
    const slug = permission?.slug || rp.permissionSlug;
    if (slug) roleSlugs.add(slug);
  });

  const enabledModules = {};
  (modules || []).forEach((module) => {
    const gatePerms = getModuleGatePermissionDetails(module);
    enabledModules[module.code] = gatePerms.some(
      (p) => p.slug && roleSlugs.has(p.slug)
    );
  });
  return enabledModules;
}

/**
 * Build permissionIds for role create/update from module enable toggles only.
 * On edit, non-view permissions are preserved from existingRolePermissions.
 */
export function buildPermissionIdsForModuleVisibilitySave({
  modules,
  enabledModules,
  existingRolePermissions = [],
  mode = "edit",
}) {
  const existingSlugs = new Set();
  (existingRolePermissions || []).forEach((rp) => {
    const permission = rp.Permission || rp.permission;
    const slug = permission?.slug || rp.permissionSlug;
    if (slug) existingSlugs.add(slug);
  });

  const ids = [];
  (modules || []).forEach((module) => {
    const enabled = !!enabledModules?.[module.code];
    const gateActions = new Set(
      getModuleGatePermissionDetails(module).map((p) => p.formKey || p.action)
    );
    (module.permissionDetails || []).forEach((perm) => {
      const action = perm.formKey || perm.action;
      const isGate = gateActions.has(action);
      let include = false;
      if (isGate) {
        include = enabled;
      } else if (mode === "create") {
        include = false;
      } else {
        // Drop all permissions for modules turned off in RBAC (not only read/view gates).
        include =
          !!enabledModules?.[module.code] &&
          perm.slug &&
          existingSlugs.has(perm.slug);
      }
      if (include && perm.id) ids.push(perm.id);
    });
  });
  return [...new Set(ids)];
}

/** View slug for hasPermission checks (e.g. exam.read). */
export function getModuleViewPermissionSlug(module) {
  const viewPerm = getModuleViewPermission(module?.permissionDetails);
  return viewPerm?.slug || null;
}
